// =========================================================================
// HelpyDoc Metabolic · Data Service (TypeScript)
// Capa de datos mock — simula llamadas a una API real
// En producción: reemplaza fetch('/data/X.json') por ApiClient
// =========================================================================
import { AuthService } from './AuthService';
import type { WeightEntry } from '@/models/weight';
import { applyPlatosOverlay } from '@/lib/platos-remote';

/** Objetivo del usuario: define el modo de la app y el sesgo de estimación calórica de la IA. */
export type UserGoal = 'lose' | 'maintain' | 'gain';

export interface Usuario {
  nombre: string;
  apellidos: string;
  email: string;
  foto?: string;
  pesoActual: number;
  pesoObjetivo: number;
  pesoInicial: number;
  altura: number;
  edad: number;
  /**
   * Opcional a propósito: mueve el umbral de perímetro abdominal (94 cm en
   * hombres, 80 en mujeres). Sin declarar NO se supone ninguno.
   *
   * La semilla traía un `genero` que NO leía ningún código y que empezó a
   * contradecir a este campo en cuanto se pudo declarar el sexo. Se retiró de
   * `public/data/usuario.json`; sigue en `public/web/` porque ese es el
   * piloto congelado. Este es el campo vivo.
   */
  sexo?: 'hombre' | 'mujer';
  /**
   * Enfermedades declaradas por la persona. NO desplazan ningún objetivo:
   * disparan la revisión humana del alta y la recomendación de valoración
   * médica. Ver `features/progreso/objetivos.ts`.
   *
   * Sustituye al `condicionesMetabolicas` de la semilla, que tampoco leía
   * nadie y que era la segunda verdad sobre lo mismo.
   */
  comorbilidades?: string[];
  goal?: UserGoal;
  /**
   * Instante ISO en que la persona TERMINÓ el wizard de onboarding (F08 G2).
   * Marca de UI para reanudar el wizard si se cierra la app a mitad — NO es
   * admisión ni activación clínica (eso lo decide la valoración/elegibilidad,
   * Tier-2). Ausente ⇒ onboarding sin completar. Ver `lib/onboarding-estado.ts`.
   */
  onboardingCompletedAt?: string;
  nutricion: {
    caloriasObjetivo: number;
    proteinasObjetivo: number;
    carbohidratosObjetivo: number;
    grasasObjetivo: number;
  };
}

/** Ítem individual de un análisis nutricional almacenado con la comida. */
export interface MealAnalisisItem {
  label: string;
  kcal: number;
  proteinG: number;
  carbsG: number;
  fatG: number;
  pesoG: number;
}

/** Resultado de análisis guardado junto a cada comida (para informes y búsqueda). */
export interface MealAnalisis {
  items: MealAnalisisItem[];
  /** Confianza global 0-1. */
  confianza: number;
  nivel: 'low' | 'medium' | 'high';
  /** Origen del análisis. */
  fuente: 'ia' | 'db_personal' | 'usda' | 'fatsecret' | 'openfoodfacts' | 'manual';
  warnings?: string[];
}

export interface Comida {
  id: string;
  fecha: string;
  hora: string;
  nombre: string;
  calorias: number;
  proteinas: number;
  carbohidratos: number;
  grasas: number;
  categoria?: string;
  /** Foto del plato (data URL en mock; URL de Storage en producción). */
  foto?: string;
  /** Análisis nutricional detallado guardado con la comida para informes. */
  analisis?: MealAnalisis;
}

export interface Emocion {
  id: string;
  fecha: string;
  hora: string;
  estado: string;
  nivel: number;
  nota?: string;
}

export interface Hidratacion {
  fecha: string;
  mlConsumidos: number;
  mlObjetivo: number;
}

export interface Ejercicio {
  id: string;
  hora: string;
  tipo: string;
  nombre: string;
  duracionMin: number;
  calorias: number;
}

export interface ActividadHoy {
  fecha?: string;
  pasos: number;
  pasosObjetivo: number;
  calorias: number;
  minutosActivos: number;
  ejercicios: Ejercicio[];
}

export interface ResumenNutricion {
  calorias: number;
  proteinas: number;
  carbohidratos: number;
  grasas: number;
  objetivos: Usuario['nutricion'];
  pctCalorias: number;
  pctProteinas: number;
  pctCarbohidratos: number;
  pctGrasas: number;
}

export interface PesoProgreso {
  pesoActual: number;
  pesoObjetivo: number;
  perdido: number;
  restante: number;
  pct: number;
}

export interface Informe {
  id: string;
  titulo: string;
  fecha: string;
  tipo: 'mensual' | 'semanal';
  estado: 'disponible' | 'pendiente';
  paginas: number;
}

export interface Plato {
  id: string;
  nombre: string;
  descripcion: string;
  categoria: string;
  calorias: number;
  proteinas: number;
  carbohidratos: number;
  grasas: number;
  tiempo: number;
  dificultad: string;
  etiquetas: string[];
  ingredientes: string[];
  imagen?: string;
  /** Macros/calorías son estimaciones no validadas clínicamente (catálogo educativo). */
  valoresOrientativos?: boolean;
  /**
   * Plato RETIRADO del catálogo por administración. No se borra: el overlay
   * (`content/platos.json`) nunca elimina platos base, así que retirar uno es
   * marcarlo. Queda oculto para el paciente y visible en el panel de admin,
   * desde donde puede volver a publicarse.
   */
  oculto?: boolean;
}

// ── Base de datos de alimentos ─────────────────────────────────────────────────
export type FoodCategory =
  | 'vegetal' | 'proteína' | 'cereal' | 'lácteo'
  | 'fruta' | 'grasa' | 'bebida' | 'plato_combinado' | 'snack' | 'otro';

export type FoodSource = 'user_analysis' | 'usda' | 'fatsecret' | 'manual_admin' | 'openfoodfacts';

export interface FoodItem {
  id: string;
  name: string;                  // nombre normalizado (minúsculas, sin tildes)
  displayName: string;           // nombre para mostrar al usuario
  aliases: string[];             // variantes y nombres alternativos
  category: FoodCategory;
  kcalPer100g: number;
  proteinPer100g: number;
  carbsPer100g: number;
  fatPer100g: number;
  fiberPer100g?: number;
  typicalServingG: number;       // porción típica en gramos
  typicalServingLabel: string;   // "1 plato", "1 pieza", "1 taza"
  source: FoodSource;
  analysisCount: number;         // veces analizado por usuarios
  verifiedByNutritionist: boolean;
  /** URL de Firebase Storage de la foto original del análisis IA (solo en alimentos del usuario). */
  photoUrl?: string;
  /** Average-hash (aHash 64-bit hex) de la foto para búsqueda por similitud visual. */
  photoHash?: string;
}

// ── Mock: 35 alimentos españoles comunes ──────────────────────────────────────
export const MOCK_FOOD_DATABASE: FoodItem[] = [
  // PLATOS COMBINADOS
  { id:'fi_001', name:'ensalada de lentejas', displayName:'Ensalada de lentejas', aliases:['lentil salad','ensalada lentejas'], category:'plato_combinado', kcalPer100g:120, proteinPer100g:8.5, carbsPer100g:15.2, fatPer100g:2.1, fiberPer100g:5.2, typicalServingG:250, typicalServingLabel:'1 plato', source:'user_analysis', analysisCount:14, verifiedByNutritionist:false },
  { id:'fi_002', name:'pollo a la plancha', displayName:'Pollo a la plancha', aliases:['grilled chicken','pechuga plancha','pollo plancha'], category:'proteína', kcalPer100g:165, proteinPer100g:31, carbsPer100g:0, fatPer100g:3.6, typicalServingG:150, typicalServingLabel:'1 filete', source:'usda', analysisCount:42, verifiedByNutritionist:true },
  { id:'fi_003', name:'tortilla de patata', displayName:'Tortilla de patata', aliases:['tortilla española','spanish omelette'], category:'plato_combinado', kcalPer100g:185, proteinPer100g:9.2, carbsPer100g:14.8, fatPer100g:9.5, typicalServingG:150, typicalServingLabel:'1 porción', source:'user_analysis', analysisCount:28, verifiedByNutritionist:false },
  { id:'fi_004', name:'gazpacho', displayName:'Gazpacho', aliases:['gazpacho andaluz'], category:'vegetal', kcalPer100g:28, proteinPer100g:0.8, carbsPer100g:4.5, fatPer100g:0.6, fiberPer100g:1.2, typicalServingG:300, typicalServingLabel:'1 vaso', source:'fatsecret', analysisCount:19, verifiedByNutritionist:true },
  { id:'fi_005', name:'salmon al horno', displayName:'Salmón al horno', aliases:['salmon horno','baked salmon'], category:'proteína', kcalPer100g:206, proteinPer100g:20, carbsPer100g:0, fatPer100g:13, fiberPer100g:0, typicalServingG:200, typicalServingLabel:'1 filete', source:'usda', analysisCount:31, verifiedByNutritionist:true },
  { id:'fi_006', name:'arroz integral', displayName:'Arroz integral cocido', aliases:['arroz integral cocido','brown rice'], category:'cereal', kcalPer100g:111, proteinPer100g:2.6, carbsPer100g:23, fatPer100g:0.9, fiberPer100g:1.8, typicalServingG:180, typicalServingLabel:'1 ración', source:'usda', analysisCount:55, verifiedByNutritionist:true },
  { id:'fi_007', name:'avena con leche', displayName:'Avena con leche', aliases:['porridge','oatmeal','avena desayuno'], category:'cereal', kcalPer100g:150, proteinPer100g:6, carbsPer100g:24, fatPer100g:3.5, fiberPer100g:2.5, typicalServingG:250, typicalServingLabel:'1 tazón', source:'user_analysis', analysisCount:38, verifiedByNutritionist:false },
  { id:'fi_008', name:'crema de calabaza', displayName:'Crema de calabaza', aliases:['sopa calabaza','pumpkin cream'], category:'vegetal', kcalPer100g:45, proteinPer100g:1.5, carbsPer100g:7.8, fatPer100g:1.2, fiberPer100g:1.5, typicalServingG:300, typicalServingLabel:'1 plato', source:'user_analysis', analysisCount:12, verifiedByNutritionist:false },
  { id:'fi_009', name:'huevos revueltos', displayName:'Huevos revueltos', aliases:['scrambled eggs','huevo revuelto'], category:'proteína', kcalPer100g:148, proteinPer100g:10, carbsPer100g:1.2, fatPer100g:11.5, typicalServingG:120, typicalServingLabel:'2 huevos', source:'usda', analysisCount:47, verifiedByNutritionist:true },
  { id:'fi_010', name:'pasta integral', displayName:'Pasta integral cocida', aliases:['espagueti integral','macarrones integrales'], category:'cereal', kcalPer100g:124, proteinPer100g:5, carbsPer100g:23.5, fatPer100g:1.1, fiberPer100g:3.5, typicalServingG:200, typicalServingLabel:'1 ración', source:'usda', analysisCount:62, verifiedByNutritionist:true },
  // LÁCTEOS
  { id:'fi_011', name:'yogur natural', displayName:'Yogur natural', aliases:['yogurt natural','plain yogurt'], category:'lácteo', kcalPer100g:61, proteinPer100g:3.5, carbsPer100g:4.7, fatPer100g:3.3, typicalServingG:125, typicalServingLabel:'1 yogur', source:'fatsecret', analysisCount:89, verifiedByNutritionist:true },
  { id:'fi_012', name:'queso fresco', displayName:'Queso fresco', aliases:['queso blanco','fresh cheese'], category:'lácteo', kcalPer100g:98, proteinPer100g:12, carbsPer100g:2.5, fatPer100g:4.5, typicalServingG:60, typicalServingLabel:'1 porción', source:'fatsecret', analysisCount:23, verifiedByNutritionist:true },
  { id:'fi_013', name:'leche desnatada', displayName:'Leche desnatada', aliases:['leche skim','skimmed milk'], category:'lácteo', kcalPer100g:35, proteinPer100g:3.4, carbsPer100g:5, fatPer100g:0.1, typicalServingG:250, typicalServingLabel:'1 vaso', source:'usda', analysisCount:44, verifiedByNutritionist:true },
  // FRUTAS
  { id:'fi_014', name:'manzana', displayName:'Manzana', aliases:['apple','manzana fresca'], category:'fruta', kcalPer100g:52, proteinPer100g:0.3, carbsPer100g:14, fatPer100g:0.2, fiberPer100g:2.4, typicalServingG:180, typicalServingLabel:'1 pieza', source:'usda', analysisCount:71, verifiedByNutritionist:true },
  { id:'fi_015', name:'platano', displayName:'Plátano', aliases:['banana','plátano maduro'], category:'fruta', kcalPer100g:89, proteinPer100g:1.1, carbsPer100g:23, fatPer100g:0.3, fiberPer100g:2.6, typicalServingG:120, typicalServingLabel:'1 pieza', source:'usda', analysisCount:93, verifiedByNutritionist:true },
  { id:'fi_016', name:'frutos rojos', displayName:'Frutos rojos mixtos', aliases:['berries','arándanos fresas frambuesas'], category:'fruta', kcalPer100g:57, proteinPer100g:0.7, carbsPer100g:12, fatPer100g:0.5, fiberPer100g:3.8, typicalServingG:100, typicalServingLabel:'1 puñado', source:'usda', analysisCount:34, verifiedByNutritionist:true },
  { id:'fi_017', name:'naranja', displayName:'Naranja', aliases:['orange','naranja fresca'], category:'fruta', kcalPer100g:47, proteinPer100g:0.9, carbsPer100g:12, fatPer100g:0.1, fiberPer100g:2.4, typicalServingG:200, typicalServingLabel:'1 pieza', source:'usda', analysisCount:58, verifiedByNutritionist:true },
  // VERDURAS / VEGETALES
  { id:'fi_018', name:'ensalada mixta', displayName:'Ensalada mixta', aliases:['ensalada verde','mixed salad'], category:'vegetal', kcalPer100g:18, proteinPer100g:1.2, carbsPer100g:2.5, fatPer100g:0.3, fiberPer100g:1.5, typicalServingG:200, typicalServingLabel:'1 plato', source:'user_analysis', analysisCount:41, verifiedByNutritionist:false },
  { id:'fi_019', name:'brocoli', displayName:'Brócoli cocido', aliases:['broccoli','brócoli al vapor'], category:'vegetal', kcalPer100g:35, proteinPer100g:2.4, carbsPer100g:5.1, fatPer100g:0.4, fiberPer100g:3.3, typicalServingG:200, typicalServingLabel:'1 ración', source:'usda', analysisCount:37, verifiedByNutritionist:true },
  { id:'fi_020', name:'espinacas', displayName:'Espinacas', aliases:['spinach','espinacas frescas'], category:'vegetal', kcalPer100g:23, proteinPer100g:2.9, carbsPer100g:3.6, fatPer100g:0.4, fiberPer100g:2.2, typicalServingG:150, typicalServingLabel:'1 ración', source:'usda', analysisCount:29, verifiedByNutritionist:true },
  // PROTEÍNAS
  { id:'fi_021', name:'atun en lata', displayName:'Atún en lata (al natural)', aliases:['canned tuna','atún natural'], category:'proteína', kcalPer100g:116, proteinPer100g:25.5, carbsPer100g:0, fatPer100g:1, typicalServingG:80, typicalServingLabel:'1 lata', source:'fatsecret', analysisCount:66, verifiedByNutritionist:true },
  { id:'fi_022', name:'legumbres cocidas', displayName:'Lentejas / garbanzos cocidos', aliases:['lentils','garbanzos','legumbres'], category:'proteína', kcalPer100g:116, proteinPer100g:9, carbsPer100g:20, fatPer100g:0.4, fiberPer100g:7.9, typicalServingG:200, typicalServingLabel:'1 ración', source:'usda', analysisCount:25, verifiedByNutritionist:true },
  { id:'fi_023', name:'pechuga de pavo', displayName:'Pechuga de pavo', aliases:['turkey breast','pavo plancha'], category:'proteína', kcalPer100g:135, proteinPer100g:29, carbsPer100g:0, fatPer100g:1.7, typicalServingG:130, typicalServingLabel:'1 filete', source:'usda', analysisCount:18, verifiedByNutritionist:true },
  // CEREALES / CARBOHIDRATOS
  { id:'fi_024', name:'pan integral', displayName:'Pan integral', aliases:['whole wheat bread','pan de centeno'], category:'cereal', kcalPer100g:247, proteinPer100g:9, carbsPer100g:45, fatPer100g:3.4, fiberPer100g:6.5, typicalServingG:40, typicalServingLabel:'2 rebanadas', source:'fatsecret', analysisCount:53, verifiedByNutritionist:false },
  { id:'fi_025', name:'patata cocida', displayName:'Patata cocida', aliases:['boiled potato','patata hervida'], category:'cereal', kcalPer100g:87, proteinPer100g:1.9, carbsPer100g:20, fatPer100g:0.1, fiberPer100g:1.8, typicalServingG:200, typicalServingLabel:'1 pieza mediana', source:'usda', analysisCount:48, verifiedByNutritionist:true },
  // GRASAS SALUDABLES
  { id:'fi_026', name:'aguacate', displayName:'Aguacate', aliases:['avocado','palta'], category:'grasa', kcalPer100g:160, proteinPer100g:2, carbsPer100g:8.5, fatPer100g:14.7, fiberPer100g:6.7, typicalServingG:100, typicalServingLabel:'½ aguacate', source:'usda', analysisCount:77, verifiedByNutritionist:true },
  { id:'fi_027', name:'nueces', displayName:'Nueces', aliases:['walnuts','nuez'], category:'grasa', kcalPer100g:654, proteinPer100g:15.2, carbsPer100g:13.7, fatPer100g:65.2, fiberPer100g:6.7, typicalServingG:30, typicalServingLabel:'1 puñado (30g)', source:'usda', analysisCount:32, verifiedByNutritionist:true },
  // BEBIDAS
  { id:'fi_028', name:'cafe solo', displayName:'Café solo', aliases:['espresso','cafe','coffee'], category:'bebida', kcalPer100g:2, proteinPer100g:0.3, carbsPer100g:0, fatPer100g:0, typicalServingG:30, typicalServingLabel:'1 taza', source:'usda', analysisCount:120, verifiedByNutritionist:true },
  // SNACKS
  { id:'fi_029', name:'hummus', displayName:'Hummus', aliases:['humus','chickpea dip'], category:'snack', kcalPer100g:177, proteinPer100g:7.9, carbsPer100g:14.3, fatPer100g:9.6, fiberPer100g:6, typicalServingG:80, typicalServingLabel:'2 cucharadas', source:'fatsecret', analysisCount:21, verifiedByNutritionist:false },
  { id:'fi_030', name:'wok de verduras', displayName:'Wok de verduras con tofu', aliases:['stir fry','salteado verduras'], category:'plato_combinado', kcalPer100g:85, proteinPer100g:5.2, carbsPer100g:9.8, fatPer100g:2.8, fiberPer100g:2.5, typicalServingG:300, typicalServingLabel:'1 plato', source:'user_analysis', analysisCount:8, verifiedByNutritionist:false },
  { id:'fi_031', name:'sandwich integral', displayName:'Sándwich integral con pavo y aguacate', aliases:['sandwich','bocadillo integral'], category:'plato_combinado', kcalPer100g:210, proteinPer100g:12, carbsPer100g:24, fatPer100g:7, fiberPer100g:4, typicalServingG:200, typicalServingLabel:'1 sándwich', source:'user_analysis', analysisCount:16, verifiedByNutritionist:false },
  { id:'fi_032', name:'smoothie verde', displayName:'Smoothie verde (espinacas + plátano)', aliases:['green smoothie','batido verde'], category:'bebida', kcalPer100g:55, proteinPer100g:1.8, carbsPer100g:11, fatPer100g:0.5, fiberPer100g:1.5, typicalServingG:400, typicalServingLabel:'1 vaso grande', source:'user_analysis', analysisCount:11, verifiedByNutritionist:false },
  { id:'fi_033', name:'caldo de pollo', displayName:'Caldo de pollo casero', aliases:['chicken broth','sopa pollo'], category:'otro', kcalPer100g:15, proteinPer100g:1.2, carbsPer100g:0.5, fatPer100g:0.5, typicalServingG:300, typicalServingLabel:'1 taza', source:'user_analysis', analysisCount:6, verifiedByNutritionist:false },
  { id:'fi_034', name:'merluza al vapor', displayName:'Merluza al vapor', aliases:['steamed hake','merluza vapor'], category:'proteína', kcalPer100g:86, proteinPer100g:17.9, carbsPer100g:0, fatPer100g:1.7, typicalServingG:200, typicalServingLabel:'1 filete', source:'fatsecret', analysisCount:13, verifiedByNutritionist:true },
  { id:'fi_035', name:'tostadas con tomate', displayName:'Tostadas con tomate y aceite', aliases:['pan con tomate','toast tomato'], category:'cereal', kcalPer100g:195, proteinPer100g:5.2, carbsPer100g:32, fatPer100g:5.8, typicalServingG:80, typicalServingLabel:'2 tostadas', source:'user_analysis', analysisCount:9, verifiedByNutritionist:false },
  // HAMBURGUESAS
  { id:'fi_036', name:'hamburguesa de pavo', displayName:'Hamburguesa de pavo', aliases:['turkey burger','burger pavo','burguer pavo'], category:'proteína', kcalPer100g:158, proteinPer100g:20.5, carbsPer100g:0, fatPer100g:8.2, typicalServingG:150, typicalServingLabel:'1 hamburguesa', source:'usda', analysisCount:22, verifiedByNutritionist:true },
  { id:'fi_037', name:'hamburguesa de cerdo', displayName:'Hamburguesa de cerdo', aliases:['pork burger','burger cerdo'], category:'proteína', kcalPer100g:263, proteinPer100g:16.9, carbsPer100g:0, fatPer100g:21.5, typicalServingG:150, typicalServingLabel:'1 hamburguesa', source:'usda', analysisCount:15, verifiedByNutritionist:true },
  { id:'fi_038', name:'hamburguesa de ternera', displayName:'Hamburguesa de ternera', aliases:['beef burger','burger ternera','hamburguesa de res','burger res'], category:'proteína', kcalPer100g:254, proteinPer100g:17.2, carbsPer100g:0, fatPer100g:20, typicalServingG:150, typicalServingLabel:'1 hamburguesa', source:'usda', analysisCount:38, verifiedByNutritionist:true },
  { id:'fi_039', name:'hamburguesa de pollo', displayName:'Hamburguesa de pollo', aliases:['chicken burger','burger pollo'], category:'proteína', kcalPer100g:172, proteinPer100g:18.5, carbsPer100g:3.2, fatPer100g:9.2, typicalServingG:150, typicalServingLabel:'1 hamburguesa', source:'usda', analysisCount:19, verifiedByNutritionist:true },
  { id:'fi_040', name:'hamburguesa vegana', displayName:'Hamburguesa vegetal / vegana', aliases:['veggie burger','burger vegana','burger vegetal'], category:'proteína', kcalPer100g:178, proteinPer100g:14.5, carbsPer100g:12.8, fatPer100g:7.9, typicalServingG:100, typicalServingLabel:'1 hamburguesa', source:'user_analysis', analysisCount:11, verifiedByNutritionist:false },
];

// ── Tipos internos ────────────────────────────────────────────────────────

interface Registros {
  comidas: Comida[];
  emociones: Emocion[];
  hidratacion: Hidratacion[];
}

interface Actividad {
  hoy: ActividadHoy;
  semana: { dia: string; fecha?: string; pasos: number; calorias: number; minutosActivos?: number }[];
  resumenMensual: object;
  platosSaludables: Plato[];
}

type Resource = 'usuario' | 'registros' | 'actividad';

// ── Implementación ────────────────────────────────────────────────────────

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutos

interface CacheEntry<T> { data: T; ts: number }
const _cache: Partial<Record<Resource, CacheEntry<unknown>>> = {};
const LS_KEY = 'helpydoc_data';
const PHOTO_KEY_PREFIX = `${LS_KEY}_photo_`;

// ── DB personal del usuario (alimentos analizados con IA, persistente) ────
// Los 40 ítems de MOCK_FOOD_DATABASE son catálogo fijo en memoria.
// Los alimentos analizados por el usuario se guardan aquí y sobreviven
// recargas: aparecen en búsquedas y evitan relanzar la IA en análisis futuros.

const USER_FOODS_LS_KEY = `${LS_KEY}_fooddb`;
let _userFoodCache: FoodItem[] | null = null;

function _loadUserFoods(): FoodItem[] {
  if (_userFoodCache !== null) return _userFoodCache;
  try {
    const raw = localStorage.getItem(USER_FOODS_LS_KEY);
    _userFoodCache = raw ? (JSON.parse(raw) as FoodItem[]) : [];
  } catch {
    _userFoodCache = [];
  }
  return _userFoodCache;
}

function _saveUserFoods(foods: FoodItem[]): void {
  _userFoodCache = foods;
  try {
    // No guardar base64 para no saturar localStorage; solo URLs de Storage.
    const slim = foods.map(f => ({
      ...f,
      photoUrl: f.photoUrl?.startsWith('data:') ? undefined : f.photoUrl,
    }));
    localStorage.setItem(USER_FOODS_LS_KEY, JSON.stringify(slim));
  } catch {
    // localStorage lleno — la DB en memoria sigue disponible en esta sesión.
  }
}


/** Guarda la foto de una comida en su propia clave de localStorage. */
function _savePhotoLS(mealId: string, foto: string): void {
  try {
    localStorage.setItem(`${PHOTO_KEY_PREFIX}${mealId}`, foto);
  } catch {
    // Foto demasiado grande o localStorage lleno — la comida se guarda igual, sin miniatura.
  }
}

/** Recupera la foto de una comida de su clave individual. */
function _loadPhotoLS(mealId: string): string | undefined {
  try {
    return localStorage.getItem(`${PHOTO_KEY_PREFIX}${mealId}`) ?? undefined;
  } catch {
    return undefined;
  }
}

function _normalize(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

/** Distancia Hamming entre dos hashes hex de 16 chars (64 bits). */
function _hammingDistance(h1: string, h2: string): number {
  if (h1.length !== h2.length) return 64;
  let d = 0;
  for (let i = 0; i < h1.length; i++) {
    let xor = (parseInt(h1[i], 16) ^ parseInt(h2[i], 16));
    while (xor) { d += xor & 1; xor >>= 1; }
  }
  return d;
}

function _hoy(): string {
  return new Date().toISOString().split('T')[0];
}

/** Distingue comidas reales del usuario de las del JSON demo.
 *  Demo: com_001, com_007 … (sufijo numérico ≤9 dígitos)
 *  Usuario: com_1750295421234 … (timestamp 13 dígitos) o Firestore ID (alfanumérico) */
function _isUserMeal(id: string): boolean {
  return !/^com_\d{1,9}$/.test(id);
}

function _horaActual(): string {
  return new Date().toTimeString().slice(0, 5);
}

function _loadLS<T>(resource: Resource): T | null {
  try {
    const raw = localStorage.getItem(`${LS_KEY}_${resource}`);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function _saveLS(resource: Resource, data: unknown): void {
  try {
    localStorage.setItem(`${LS_KEY}_${resource}`, JSON.stringify(data));
  } catch (err) {
    console.warn('[DataService] localStorage no disponible:', err);
  }
}

function _merge(resource: Resource, base: unknown, saved: unknown): unknown {
  if (!saved) return base;
  if (resource === 'registros') {
    const b = base as Registros;
    const s = saved as Partial<Registros>;
    return {
      comidas: [...(s.comidas ?? []), ...(b.comidas ?? [])].filter(
        (v, i, a) => a.findIndex(x => x.id === v.id) === i,
      ),
      emociones: [...(s.emociones ?? []), ...(b.emociones ?? [])].filter(
        (v, i, a) => a.findIndex(x => x.id === v.id) === i,
      ),
      hidratacion: s.hidratacion ?? b.hidratacion,
    };
  }
  if (resource === 'actividad') {
    const b = base as Actividad;
    const s = saved as Partial<Actividad>;
    return {
      ...b,
      hoy: s.hoy
        ? {
            ...b.hoy,
            ...s.hoy,
            ejercicios: [...(s.hoy.ejercicios ?? []), ...(b.hoy?.ejercicios ?? [])].filter(
              (v, i, a) => a.findIndex(x => x.id === v.id) === i,
            ),
          }
        : b.hoy,
    };
  }
  return { ...(base as object), ...(saved as object) };
}

// ── Defaults offline / tests ──────────────────────────────────────────────
// Usados cuando fetch('/data/X.json') falla (sin servidor, tests, offline).
// Para 'usuario', inyecta el email de la sesión activa si existe.

const _DEFAULT_REGISTROS: Registros = { comidas: [], emociones: [], hidratacion: [] };

const _DEFAULT_ACTIVIDAD: Actividad = {
  hoy: { fecha: '', ejercicios: [], pasos: 0, pasosObjetivo: 10_000, calorias: 0, minutosActivos: 0 },
  semana: [],
  resumenMensual: {},
  platosSaludables: [],
};

/** Edad cumplida a partir de una fecha ISO de nacimiento. */
function _edadDesde(fechaNacimiento: string): number | undefined {
  const nacio = new Date(fechaNacimiento);
  if (Number.isNaN(nacio.getTime())) return undefined;
  const hoy = new Date();
  let edad = hoy.getFullYear() - nacio.getFullYear();
  // Resta el año si aún no ha llegado el cumpleaños: sin esto, medio año de
  // usuarios sale con un año de más, y eso mueve el rango de IMC por edad.
  const m = hoy.getMonth() - nacio.getMonth();
  if (m < 0 || (m === 0 && hoy.getDate() < nacio.getDate())) edad -= 1;
  return edad > 0 && edad < 130 ? edad : undefined;
}

function _defaultUsuario(): Usuario {
  const s = AuthService.getSession();
  return {
    nombre:       s?.nombre    ?? 'Usuario',
    apellidos:    '',
    email:        s?.email     ?? 'demo@helpydoc.com',
    pesoInicial:  85,
    pesoActual:   82,
    pesoObjetivo: 72,
    altura:       170,
    edad:         32,
    // El usuario demo/seed es una cuenta ya establecida: se considera onboarded
    // para que la demo entre directa a la app (no al wizard). Ver F08 G2.
    onboardingCompletedAt: '2026-01-10T00:00:00.000Z',
    nutricion: {
      caloriasObjetivo:      1_800,
      proteinasObjetivo:     150,
      carbohidratosObjetivo: 180,
      grasasObjetivo:        60,
    },
  };
}

function _invalidate(resource: Resource): void {
  delete _cache[resource];
}

function _isCacheValid(resource: Resource): boolean {
  const entry = _cache[resource];
  return !!entry && (Date.now() - entry.ts) < CACHE_TTL_MS;
}

/**
 * Desplaza todas las fechas de registros para que el día más reciente
 * del JSON sea siempre "hoy". Esto hace que los datos mock nunca queden
 * desactualizados aunque el JSON no se actualice.
 */
function _shiftRegistrosDates(registros: Registros): Registros {
  const hoy = _hoy();
  const allDates = [
    ...registros.comidas.map(c => c.fecha),
    ...registros.emociones.map(e => e.fecha),
    ...registros.hidratacion.map(h => h.fecha),
  ].filter(Boolean).sort();
  if (allDates.length === 0) return registros;
  const maxDate = allDates[allDates.length - 1];
  if (maxDate >= hoy) return registros; // datos ya están al día
  const msPerDay = 86_400_000;
  const daysDiff = Math.round(
    (new Date(`${hoy}T12:00:00`).getTime() - new Date(`${maxDate}T12:00:00`).getTime()) / msPerDay,
  );
  const shiftDate = (fecha: string): string => {
    const d = new Date(`${fecha}T12:00:00`);
    d.setDate(d.getDate() + daysDiff);
    return d.toISOString().split('T')[0];
  };
  return {
    comidas:    registros.comidas.map(c => ({ ...c, fecha: shiftDate(c.fecha) })),
    emociones:  registros.emociones.map(e => ({ ...e, fecha: shiftDate(e.fecha) })),
    hidratacion: registros.hidratacion.map(h => ({ ...h, fecha: shiftDate(h.fecha) })),
  };
}

/** Igual para actividad: desplaza hoy.fecha y semana[].fecha al día actual. */
function _shiftActividadDates(actividad: Actividad): Actividad {
  const hoy = _hoy();
  if (!actividad.hoy?.fecha || actividad.hoy.fecha >= hoy) return actividad;
  const msPerDay = 86_400_000;
  const daysDiff = Math.round(
    (new Date(`${hoy}T12:00:00`).getTime() - new Date(`${actividad.hoy.fecha}T12:00:00`).getTime()) / msPerDay,
  );
  const shiftDate = (fecha: string): string => {
    const d = new Date(`${fecha}T12:00:00`);
    d.setDate(d.getDate() + daysDiff);
    return d.toISOString().split('T')[0];
  };
  return {
    ...actividad,
    hoy: { ...actividad.hoy, fecha: hoy },
    semana: (actividad.semana ?? []).map(s => ({
      ...s,
      fecha: s.fecha ? shiftDate(s.fecha) : s.fecha,
    })),
  };
}

async function _load<T>(resource: Resource): Promise<T> {
  if (_isCacheValid(resource)) return _cache[resource]!.data as T;

  // Intentar obtener datos base del servidor
  let base: unknown;
  try {
    const res = await fetch(`/data/${resource}.json`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    base = await res.json();
  } catch (err) {
    // Fallback offline / tests: usar estructura vacía con sesión activa
    console.warn(`[DataService] Sin servidor para '${resource}', usando datos locales:`, (err as Error).message);
    base = resource === 'registros' ? _DEFAULT_REGISTROS
         : resource === 'actividad' ? _DEFAULT_ACTIVIDAD
         : _defaultUsuario();
  }

  const saved = _loadLS(resource);
  let merged = _merge(resource, base, saved);
  // Desplazar fechas automáticamente si los datos del JSON son antiguos
  if (resource === 'registros') {
    merged = _shiftRegistrosDates(merged as Registros);
    // Re-adjuntar fotos guardadas por separado en claves individuales.
    const reg = merged as Registros;
    merged = {
      ...reg,
      comidas: reg.comidas.map(c => ({ ...c, foto: c.foto ?? _loadPhotoLS(c.id) })),
    };
  }
  if (resource === 'actividad') merged = _shiftActividadDates(merged as Actividad);
  _cache[resource] = { data: merged, ts: Date.now() };
  return merged as T;
}

// ── API Pública ───────────────────────────────────────────────────────────

export const DataService = {
  // ── Lectura ──
  async getUsuario(): Promise<Usuario> {
    const u = await _load<Usuario>('usuario');
    // La semilla guarda `fechaNacimiento` y no `edad`, así que `u.edad` venía
    // indefinida y todo lo que dependa de ella se caía EN SILENCIO: el plan de
    // objetivos devolvía null y la gráfica salía sin trayectoria, sin error.
    if (!(u.edad > 0)) {
      const nacimiento = (u as Usuario & { fechaNacimiento?: string }).fechaNacimiento;
      const edad = nacimiento ? _edadDesde(nacimiento) : undefined;
      if (edad != null) return { ...u, edad };
    }
    return u;
  },

  async getPesoProgreso(): Promise<PesoProgreso> {
    const u = await DataService.getUsuario();
    const perdido = +(u.pesoInicial - u.pesoActual).toFixed(1);
    const restante = +(u.pesoActual - u.pesoObjetivo).toFixed(1);
    // Guarda contra división por 0 (objetivo == inicial, p.ej. "mantener") → NaN/Infinity.
    const rango = u.pesoInicial - u.pesoObjetivo;
    const pct = rango === 0 ? 100 : Math.max(0, Math.min(100, Math.round((perdido / rango) * 100)));
    return { pesoActual: u.pesoActual, pesoObjetivo: u.pesoObjetivo, perdido, restante, pct };
  },

  /** Serie histórica de peso (WeightEntry[]). Mock: sintetiza 8 semanas entre inicial y actual. */
  async getPesoHistorico(): Promise<WeightEntry[]> {
    const u = await DataService.getUsuario();
    const weeks = 8;
    return Array.from({ length: weeks }, (_, i) => {
      const t = i / Math.max(weeks - 1, 1);
      const weight = Math.round((u.pesoInicial - (u.pesoInicial - u.pesoActual) * t) * 10) / 10;
      const d = new Date();
      d.setDate(d.getDate() - (weeks - 1 - i) * 7);
      return { week: `S${i + 1}`, weight, date: d.toISOString().split('T')[0] };
    });
  },

  async getComidasHoy(): Promise<Comida[]> {
    const r = await _load<Registros>('registros');
    return r.comidas
      .filter(c => c.fecha === _hoy() && _isUserMeal(c.id))
      .map(c => ({ ...c, foto: _loadPhotoLS(c.id) ?? undefined }));
  },

  async getComidasRecientes(n = 5): Promise<Comida[]> {
    const r = await _load<Registros>('registros');
    return r.comidas.slice(0, n);
  },

  async getAllComidas(): Promise<Comida[]> {
    const r = await _load<Registros>('registros');
    return r.comidas
      .filter(c => _isUserMeal(c.id))
      .map(c => ({ ...c, foto: _loadPhotoLS(c.id) ?? undefined }));
  },

  async getResumenNutricionHoy(): Promise<ResumenNutricion> {
    const [comidas, usuario] = await Promise.all([
      DataService.getComidasHoy(),
      DataService.getUsuario(),
    ]);
    const obj = usuario.nutricion;
    const totales = comidas.reduce(
      (acc, c) => ({
        calorias: acc.calorias + (c.calorias ?? 0),
        proteinas: acc.proteinas + (c.proteinas ?? 0),
        carbohidratos: acc.carbohidratos + (c.carbohidratos ?? 0),
        grasas: acc.grasas + (c.grasas ?? 0),
      }),
      { calorias: 0, proteinas: 0, carbohidratos: 0, grasas: 0 },
    );
    return {
      ...totales,
      objetivos: obj,
      pctCalorias: Math.min(100, Math.round((totales.calorias / obj.caloriasObjetivo) * 100)),
      pctProteinas: Math.min(100, Math.round((totales.proteinas / obj.proteinasObjetivo) * 100)),
      pctCarbohidratos: Math.min(100, Math.round((totales.carbohidratos / obj.carbohidratosObjetivo) * 100)),
      pctGrasas: Math.min(100, Math.round((totales.grasas / obj.grasasObjetivo) * 100)),
    };
  },

  async getHidratacionHoy(): Promise<Hidratacion & { pct: number }> {
    const r = await _load<Registros>('registros');
    const registro = r.hidratacion.find(h => h.fecha === _hoy()) ?? { fecha: _hoy(), mlConsumidos: 0, mlObjetivo: 2500 };
    return { ...registro, pct: Math.min(100, Math.round((registro.mlConsumidos / registro.mlObjetivo) * 100)) };
  },

  async getActividadHoy(): Promise<ActividadHoy | null> {
    const a = await _load<Actividad>('actividad');
    return a.hoy ?? null;
  },

  async getSemanaActividad(): Promise<Actividad['semana']> {
    const a = await _load<Actividad>('actividad');
    return a.semana ?? [];
  },

  async getResumenMensual(): Promise<object | null> {
    const a = await _load<Actividad>('actividad');
    return a.resumenMensual ?? null;
  },

  async getEmocionHoy(): Promise<Emocion | null> {
    const r = await _load<Registros>('registros');
    return r.emociones.find(e => e.fecha === _hoy()) ?? null;
  },

  async getHistorialEmocional(n = 7): Promise<Emocion[]> {
    const r = await _load<Registros>('registros');
    return r.emociones.slice(0, n);
  },

  /**
   * Catálogo BASE, sin overlay: los platos que viajan en el bundle. Lo usa el
   * panel de administración para distinguir un plato base (que solo se puede
   * retirar) de uno añadido desde el propio panel (que sí se puede borrar).
   */
  async getPlatosBase(): Promise<Plato[]> {
    const a = await _load<Actividad>('actividad');
    return a.platosSaludables ?? [];
  },

  async getPlatosSaludables(
    filtros: {
      categoria?: string;
      etiqueta?: string;
      busqueda?: string;
      /** Solo el panel de administración: incluye los platos retirados. */
      incluirOcultos?: boolean;
    } = {},
  ): Promise<Plato[]> {
    const a = await _load<Actividad>('actividad');
    // Overlay remoto editable desde Drive (merge por id); no-breaking si no hay.
    let platos = await applyPlatosOverlay(a.platosSaludables ?? []);
    // Los retirados por administración no llegan al paciente. Se filtran ANTES
    // que nada para que ningún otro filtro pueda colar uno oculto.
    if (!filtros.incluirOcultos) platos = platos.filter(p => !p.oculto);
    if (filtros.categoria && filtros.categoria !== 'todos') {
      platos = platos.filter(p => p.categoria === filtros.categoria);
    }
    if (filtros.etiqueta) {
      platos = platos.filter(p => p.etiquetas?.includes(filtros.etiqueta!));
    }
    if (filtros.busqueda) {
      const q = filtros.busqueda.toLowerCase();
      platos = platos.filter(
        p =>
          p.nombre.toLowerCase().includes(q) ||
          p.descripcion.toLowerCase().includes(q) ||
          p.ingredientes?.some(i => i.toLowerCase().includes(q)),
      );
    }
    return platos;
  },

  async getDashboardResumen() {
    const [usuario, nutricion, actividad, emocion, hidratacion] = await Promise.all([
      DataService.getUsuario(),
      DataService.getResumenNutricionHoy(),
      DataService.getActividadHoy(),
      DataService.getEmocionHoy(),
      DataService.getHidratacionHoy(),
    ]);
    return { usuario, nutricion, actividad, emocion, hidratacion, fecha: _hoy() };
  },

  getInformes(): Informe[] {
    return [
      { id: 'inf_001', titulo: 'Informe mensual — Mayo 2026', fecha: '2026-05-26', tipo: 'mensual', estado: 'disponible', paginas: 8 },
      { id: 'inf_002', titulo: 'Análisis glucémico — Sem. 21', fecha: '2026-05-22', tipo: 'semanal', estado: 'disponible', paginas: 4 },
      { id: 'inf_003', titulo: 'Progreso metabólico — Abril 2026', fecha: '2026-04-30', tipo: 'mensual', estado: 'disponible', paginas: 9 },
      { id: 'inf_004', titulo: 'Informe mensual — Marzo 2026', fecha: '2026-03-31', tipo: 'mensual', estado: 'disponible', paginas: 7 },
    ];
  },

  // ── Escritura ──
  async guardarComida(comida: Omit<Comida, 'id' | 'fecha' | 'hora'> & { id?: string; fecha?: string; hora?: string }): Promise<Comida> {
    const r = await _load<Registros>('registros');
    const nueva: Comida = { id: `com_${Date.now()}`, fecha: _hoy(), hora: _horaActual(), ...comida };

    // La foto se guarda en su propia clave (helpydoc_data_photo_<id>) para no
    // acumular base64 en la clave principal y evitar QuotaExceededError.
    if (nueva.foto) _savePhotoLS(nueva.id, nueva.foto);

    // Migrar fotos embebidas de comidas anteriores a claves individuales.
    for (const c of r.comidas ?? []) {
      if (c.foto && !_loadPhotoLS(c.id)) _savePhotoLS(c.id, c.foto);
    }

    // Guardar metadatos SIN foto en la clave principal.
    const comidasSinFoto = [nueva, ...(r.comidas ?? [])].map(c => ({ ...c, foto: undefined as string | undefined }));
    _saveLS('registros', { comidas: comidasSinFoto, emociones: r.emociones ?? [], hidratacion: r.hidratacion });
    _invalidate('registros');
    return nueva;
  },

  /** Actualiza campos editables de una comida ya guardada (por id). La foto no se toca. */
  async actualizarComida(
    id: string,
    patch: Partial<Pick<Comida, 'nombre' | 'calorias' | 'proteinas' | 'carbohidratos' | 'grasas' | 'analisis'>>,
  ): Promise<Comida | null> {
    const r = await _load<Registros>('registros');
    const comidas = r.comidas ?? [];
    const idx = comidas.findIndex(c => c.id === id);
    if (idx < 0) return null;
    const updated: Comida = { ...comidas[idx], ...patch };
    const nuevas = comidas.map((c, i) => (i === idx ? { ...updated, foto: undefined as string | undefined } : c));
    _saveLS('registros', { comidas: nuevas, emociones: r.emociones ?? [], hidratacion: r.hidratacion });
    _invalidate('registros');
    // Re-adjuntar la foto (los registros se guardan sin foto en su propia clave).
    return { ...updated, foto: _loadPhotoLS(id) ?? undefined };
  },

  async guardarEmocion(emocion: Omit<Emocion, 'id' | 'fecha' | 'hora'>): Promise<Emocion> {
    const r = await _load<Registros>('registros');
    const nueva: Emocion = { id: `emo_${Date.now()}`, fecha: _hoy(), hora: _horaActual(), ...emocion };
    const emociones = [nueva, ...(r.emociones ?? [])];
    _saveLS('registros', { comidas: r.comidas ?? [], emociones, hidratacion: r.hidratacion });
    _invalidate('registros');
    return nueva;
  },

  async actualizarHidratacion(mlAnadir: number): Promise<Hidratacion> {
    const r = await _load<Registros>('registros');
    const hoy = _hoy();
    const hidratacion = [...(r.hidratacion ?? [])];
    const idx = hidratacion.findIndex(h => h.fecha === hoy);
    if (idx >= 0) {
      hidratacion[idx] = { ...hidratacion[idx], mlConsumidos: Math.min(hidratacion[idx].mlObjetivo, hidratacion[idx].mlConsumidos + mlAnadir) };
    } else {
      hidratacion.unshift({ fecha: hoy, mlConsumidos: mlAnadir, mlObjetivo: 2500 });
    }
    _saveLS('registros', { comidas: r.comidas ?? [], emociones: r.emociones ?? [], hidratacion });
    _invalidate('registros');
    return hidratacion.find(h => h.fecha === hoy)!;
  },

  async guardarEjercicio(ejercicio: Omit<Ejercicio, 'id' | 'hora'>): Promise<Ejercicio> {
    const a = await _load<Actividad>('actividad');
    const nuevo: Ejercicio = { id: `ej_${Date.now()}`, hora: _horaActual(), ...ejercicio };
    const hoyData: ActividadHoy = {
      ...(a.hoy ?? { pasos: 0, pasosObjetivo: 10000, calorias: 0, minutosActivos: 0, ejercicios: [] }),
      ejercicios: [nuevo, ...(a.hoy?.ejercicios ?? [])],
      calorias: (a.hoy?.calorias ?? 0) + (ejercicio.calorias ?? 0),
      minutosActivos: (a.hoy?.minutosActivos ?? 0) + (ejercicio.duracionMin ?? 0),
    };
    _saveLS('actividad', { hoy: hoyData });
    _invalidate('actividad');
    return nuevo;
  },

  async actualizarPeso(kg: number): Promise<Usuario> {
    const u = await _load<Usuario>('usuario');
    const actualizado = { ...u, pesoActual: kg };
    _saveLS('usuario', actualizado);
    _invalidate('usuario');
    return actualizado;
  },

  async actualizarUsuario(changes: Partial<Omit<Usuario, 'email'>>): Promise<Usuario> {
    const u = await _load<Usuario>('usuario');
    const actualizado: Usuario = {
      ...u,
      ...changes,
      nutricion: changes.nutricion ? { ...u.nutricion, ...changes.nutricion } : u.nutricion,
    };
    _saveLS('usuario', actualizado);
    _invalidate('usuario');
    return actualizado;
  },

  limpiarDatos(): void {
    (['registros', 'actividad', 'usuario'] as Resource[]).forEach(r => {
      localStorage.removeItem(`${LS_KEY}_${r}`);
      _invalidate(r);
    });
  },

  // ── Base de datos de alimentos ──────────────────────────────────────────────

  /**
   * Busca alimentos en la base de datos local (mock).
   * En producción se reemplaza por ApiClient.searchFoods() vía data-adapter.
   * Normaliza la búsqueda: minúsculas, sin tildes, parcial.
   */
  /** Actualiza la foto almacenada de una comida ya guardada (p. ej. tras subir a Firebase Storage). */
  async actualizarFotoComida(mealId: string, photoUrl: string): Promise<void> {
    _savePhotoLS(mealId, photoUrl);
    _invalidate('registros');
  },

  /**
   * Elimina solo la foto de una comida sin borrar la comida.
   *
   * Devuelve la URL que tenía. No es un extra de cortesía: esa URL es lo ÚNICO
   * que identifica el objeto en Storage —las fotos se nombran por marca de
   * tiempo, no por comida—, así que sin devolverla el llamador no puede borrarlo
   * y se queda en el bucket para siempre.
   */
  async borrarFotoComida(mealId: string): Promise<string | undefined> {
    const anterior = _loadPhotoLS(mealId);
    localStorage.removeItem(`${PHOTO_KEY_PREFIX}${mealId}`);
    _invalidate('registros');
    return anterior;
  },

  /**
   * Elimina una comida (y su foto en localStorage) por ID.
   * Devuelve la URL de la foto que tenía — mismo motivo que `borrarFotoComida`.
   */
  async borrarComida(id: string): Promise<string | undefined> {
    const r = await _load<Registros>('registros');
    const borrada = (r.comidas ?? []).find(c => c.id === id);
    const updated = (r.comidas ?? []).filter(c => c.id !== id);
    localStorage.removeItem(`${PHOTO_KEY_PREFIX}${id}`);
    _saveLS('registros', { ...r, comidas: updated });

    // La foto de esta comida se borra también de Storage (lo hace el adaptador),
    // así que cualquier alimento de la base personal que la estuviera usando se
    // queda apuntando a un objeto que ya no existe. Sin esta limpieza, elegir
    // ese alimento a mano enseñaba el glifo rojo de imagen rota: el dato
    // sobrevivía a la imagen. Se quita la URL, no el alimento — sus valores
    // nutricionales siguen siendo válidos.
    if (borrada?.foto) {
      const foods = _loadUserFoods();
      let tocado = false;
      for (const f of foods) {
        if (f.photoUrl === borrada.foto) { delete f.photoUrl; tocado = true; }
      }
      if (tocado) _saveUserFoods(foods);
    }

    _invalidate('registros');
    return borrada?.foto;
  },

  /**
   * Busca en la DB personal del usuario alimentos visualmente similares a una foto.
   * Compara el average-hash (aHash) recibido con los hashes almacenados.
   * Devuelve resultados ordenados por similitud (más similar primero).
   */
  buscarPorHash(hash: string, maxDistancia = 10): { food: FoodItem; distancia: number }[] {
    return _loadUserFoods()
      .filter(f => !!f.photoHash)
      .map(f => ({ food: f, distancia: _hammingDistance(hash, f.photoHash!) }))
      .filter(r => r.distancia <= maxDistancia)
      .sort((a, b) => a.distancia - b.distancia);
  },

  async buscarAlimentos(query: string): Promise<FoodItem[]> {
    if (!query || query.trim().length < 2) return [];
    const norm = _normalize(query);
    // La DB personal del usuario tiene prioridad: aparece primero en resultados.
    const all = [..._loadUserFoods(), ...MOCK_FOOD_DATABASE];
    return all
      .filter(f =>
        _normalize(f.name).includes(norm) ||
        _normalize(f.displayName).includes(norm) ||
        f.aliases.some(a => _normalize(a).includes(norm)),
      )
      .filter((f, i, arr) => arr.findIndex(x => x.id === f.id) === i) // dedup
      .slice(0, 10);
  },

  /**
   * Registra un alimento analizado en la DB personal (localStorage).
   * La DB personal es el primer recurso consultado en búsquedas futuras,
   * evitando relanzar la IA para alimentos ya analizados.
   * En producción también lo envía al backend vía data-adapter.
   */
  async contribuirAlimento(food: Omit<FoodItem, 'id' | 'analysisCount' | 'verifiedByNutritionist'>): Promise<FoodItem> {
    const norm = _normalize(food.name);
    const userFoods = _loadUserFoods();

    // Si ya existe en la DB personal, incrementar contador y actualizar foto/hash si faltan.
    const existingUser = userFoods.find(f => _normalize(f.name) === norm);
    if (existingUser) {
      existingUser.analysisCount += 1;
      if (food.photoUrl && !existingUser.photoUrl) existingUser.photoUrl = food.photoUrl;
      if (food.photoHash && !existingUser.photoHash) existingUser.photoHash = food.photoHash;
      _saveUserFoods(userFoods);
      return existingUser;
    }

    // Si existe en el catálogo fijo, solo incrementar contador (sin foto —
    // los ítems del catálogo no son análisis del usuario).
    const existingBuiltin = MOCK_FOOD_DATABASE.find(f => _normalize(f.name) === norm);
    if (existingBuiltin) {
      existingBuiltin.analysisCount += 1;
      return existingBuiltin;
    }

    // Nuevo alimento personal.
    const nuevo: FoodItem = {
      ...food,
      id: `fi_u${Date.now()}`,
      analysisCount: 1,
      verifiedByNutritionist: false,
    };
    _saveUserFoods([nuevo, ...userFoods]);
    return nuevo;
  },
};
